import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Chip, Grid, ButtonGroup,
  CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody, TableFooter,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  FileDownload as ExportIcon,
  Print as PrintIcon,
  Description as ManifestIcon,
  HourglassTop as PendingIcon,
  CheckCircle as ExecutedIcon,
  Cancel as CancelledIcon,
} from '@mui/icons-material';
import DateFilterBar, { DEFAULT_RANGE } from '../components/DateFilterBar';
import { fetchFclList, fetchFclDetail, exportExcel, todayStr } from '../utils/reportsApi';

const FCL_STATUS_COLORS = {
  PENDING_APPROVAL: { bg: '#fef3c7', fg: '#92400e' },
  APPROVED:         { bg: '#ede9fe', fg: '#5b21b6' },
  EXECUTED:         { bg: '#ede9fe', fg: '#5b21b6' },
  CANCELLED:        { bg: '#fee2e2', fg: '#991b1b' },
  DRAFT:            { bg: '#f1f5f9', fg: '#475569' },
};

function FclStatusPill({ status }) {
  const c = FCL_STATUS_COLORS[status] || { bg: '#f1f5f9', fg: '#475569' };
  return (
    <Chip label={status.replace('_', ' ')} size="small" sx={{
      bgcolor: c.bg, color: c.fg, fontWeight: 700, fontSize: 10.5,
      height: 22, '& .MuiChip-label': { px: 1.25 },
    }} />
  );
}

function GoNoChip({ no, prefix }) {
  if (!no) return <span>—</span>;
  const p = prefix || no.split('-')[0];
  const colors = {
    RPK: { bg: '#f5edf7', fg: '#8E44AD' },
    RPT: { bg: '#fce7f3', fg: '#be185d' },
    RPS: { bg: '#f3e8ff', fg: '#7e22ce' },
    QC:  { bg: '#cffafe', fg: '#0e7490' },
    FCL: { bg: '#ede9fe', fg: '#5b21b6' },
  };
  const c = colors[p] || { bg: '#f1f5f9', fg: '#475569' };
  return (
    <Box component="span" sx={{
      fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 11.5,
      bgcolor: c.bg, color: c.fg, px: 0.875, py: 0.25, borderRadius: 0.5,
      display: 'inline-block', mr: 0.5,
    }}>
      {no}
    </Box>
  );
}

function KpiCard({ label, value, subtitle, icon: Icon, color }) {
  return (
    <Paper variant="outlined" sx={{
      p: 1.75, borderLeft: `4px solid ${color}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5, color: '#64748b', textTransform: 'uppercase', mb: 0.5 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', lineHeight: 1.1 }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 11.5, color: '#94a3b8', mt: 0.5 }}>{subtitle}</Typography>
          )}
        </Box>
        {Icon && <Icon sx={{ color, fontSize: 22, mt: 0.25 }} />}
      </Box>
    </Paper>
  );
}

// ============================================================================
//  Main FCLManifestReport
// ============================================================================
export default function FCLManifestReport({ user, showToast, prefilledFcl }) {
  const [dateFilter, setDateFilter] = useState(DEFAULT_RANGE);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true); setError('');
    const res = await fetchFclList(dateFilter.from, dateFilter.to);
    if (res.success) setList(res.data);
    else setError(res.error);
    setLoading(false);
  }, [dateFilter.from, dateFilter.to]);

  useEffect(() => { loadList(); }, [loadList]);

  // If prefilledFcl was passed, jump to detail view
  useEffect(() => {
    if (prefilledFcl) handleSelectFcl(prefilledFcl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledFcl]);

  const handleSelectFcl = async (fclNo) => {
    setDetailLoading(true); setDetail(null);
    const res = await fetchFclDetail(fclNo);
    if (res.success) setDetail(res.data);
    else showToast?.({ message: 'Could not load FCL detail: ' + res.error, severity: 'error' });
    setDetailLoading(false);
  };

  const filtered = useMemo(() => {
    if (!list) return [];
    return list.fcls.filter(f => {
      if (statusFilter !== 'all' && f.status !== statusFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = [f.fcl_no, f.buyer, f.container_no, f.destination_port, f.submitted_by]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [list, statusFilter, search]);

  // ----- DETAIL VIEW -----
  if (detail || detailLoading) {
    return <FCLDetailView
      fcl={detail}
      loading={detailLoading}
      onBack={() => { setDetail(null); }}
      showToast={showToast}
    />;
  }

  // ----- LIST VIEW -----
  const handleExport = () => {
    const headers = ['FCL No', 'Status', 'Buyer', 'Container', 'Seal No', 'Destination',
                     'Departure Date', 'Departure Time', 'Pallets', 'Cartons', 'Total KG', 'Total LB',
                     'Submitted By', 'Submitted At', 'Approved By', 'Approved At', 'Source RPKs'];
    const rows = filtered.map(f => [
      f.fcl_no, f.status, f.buyer, f.container_no, f.seal_no, f.destination_port,
      f.departure_date, f.departure_time, f.pallets, f.cartons, f.total_kgs, f.total_pounds,
      f.submitted_by, f.submitted_at, f.approved_by || '', f.approved_at || '',
      f.source_docs.join(', '),
    ]);
    const summary = list?.summary || {};
    const summaryRows = [
      ['Date Range', `${dateFilter.from} to ${dateFilter.to}`],
      ['Status Filter', statusFilter === 'all' ? 'All' : statusFilter],
      ['Total FCLs', summary.total],
      ['Pending Approval', summary.pending],
      ['Executed', summary.executed],
      ['Cancelled', summary.cancelled],
      ['Total Pallets', summary.total_pallets],
      ['Total Cartons', summary.total_cartons],
      ['Total KG', summary.total_kgs],
    ];
    exportExcel({
      filename: `fcl_list_${dateFilter.from}_to_${dateFilter.to}_${todayStr()}.xlsx`,
      sheets: [
        { name: 'FCL List', headers, rows },
        { name: 'Summary', headers: ['Metric', 'Value'], rows: summaryRows },
      ],
    });
    showToast?.({ message: '📥 Excel exported', severity: 'success' });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
          📋 FCL Manifest Report
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
          All shipment manifests · click any FCL row to see the full export-ready manifest
        </Typography>
      </Box>

      <DateFilterBar
        value={dateFilter}
        onChange={setDateFilter}
        onExport={handleExport}
        exportLabel="Export FCL List"
      />

      {/* KPI cards */}
      {list && (
        <Grid container spacing={1.25} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="Total FCLs" value={list.summary.total}
                     subtitle={`${list.summary.total_pallets} pallets · ${list.summary.total_cartons.toLocaleString()} ctn`}
                     icon={ManifestIcon} color="#1F618D" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="Pending Approval" value={list.summary.pending}
                     subtitle="awaiting approver" icon={PendingIcon} color="#ea580c" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="Executed (Shipped)" value={list.summary.executed}
                     subtitle="approved & shipped" icon={ExecutedIcon} color="#7c3aed" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="Cancelled" value={list.summary.cancelled}
                     subtitle="rejected / cancelled" icon={CancelledIcon} color="#dc2626" />
          </Grid>
        </Grid>
      )}

      {/* Sub-filters */}
      <Paper variant="outlined" sx={{ px: 1.5, py: 1, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: '#64748b', textTransform: 'uppercase' }}>
          Status
        </Typography>
        <ButtonGroup size="small">
          {[
            { id: 'all',              label: 'All' },
            { id: 'PENDING_APPROVAL', label: '⏳ Pending' },
            { id: 'EXECUTED',         label: '✓ Executed' },
            { id: 'CANCELLED',        label: '✗ Cancelled' },
          ].map(s => (
            <Button key={s.id}
                    variant={statusFilter === s.id ? 'contained' : 'outlined'}
                    onClick={() => setStatusFilter(s.id)}
                    sx={{
                      fontSize: 11.5, textTransform: 'none', py: 0.25, px: 1.25,
                      ...(statusFilter === s.id ? { bgcolor: '#8E44AD', '&:hover': { bgcolor: '#7d3c98' } } : {}),
                    }}>
              {s.label}
            </Button>
          ))}
        </ButtonGroup>

        <TextField
          size="small"
          placeholder="Search FCL no, buyer, container, port..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220, ml: 'auto', '& input': { fontSize: 12 } }}
        />
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {list && !loading && (
        <Paper variant="outlined">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell>FCL No</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Buyer</TableCell>
                  <TableCell>Container</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Departure</TableCell>
                  <TableCell align="right">Pallets</TableCell>
                  <TableCell align="right">Cartons</TableCell>
                  <TableCell align="right">KG</TableCell>
                  <TableCell>Submitted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: '#64748b' }}>
                      No FCLs match the filters in {dateFilter.from} → {dateFilter.to}
                    </TableCell>
                  </TableRow>
                ) : filtered.map(f => (
                  <TableRow key={f.id} hover sx={{ cursor: 'pointer' }}
                            onClick={() => handleSelectFcl(f.fcl_no)}>
                    <TableCell><GoNoChip no={f.fcl_no} prefix="FCL" /></TableCell>
                    <TableCell><FclStatusPill status={f.status} /></TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{f.buyer || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5 }}>{f.container_no || '—'}</TableCell>
                    <TableCell>{f.destination_port || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5 }}>
                      {f.departure_date}
                      <br />
                      <Box component="span" sx={{ color: '#94a3b8', fontSize: 10.5 }}>
                        {f.departure_time}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{f.pallets}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{f.cartons.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{f.total_kgs.toFixed(2)}</TableCell>
                    <TableCell sx={{ fontSize: 11.5, color: '#64748b' }}>
                      {f.submitted_by}<br />
                      <Box component="span" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5 }}>
                        {f.submitted_at?.slice(0, 16)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {filtered.length > 0 && (
                <TableFooter>
                  <TableRow sx={{ bgcolor: '#fafbfc', fontWeight: 700 }}>
                    <TableCell colSpan={6}>Total ({filtered.length} FCLs)</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#0f172a' }}>
                      {filtered.reduce((s, f) => s + f.pallets, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#0f172a' }}>
                      {filtered.reduce((s, f) => s + f.cartons, 0).toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#0f172a' }}>
                      {filtered.reduce((s, f) => s + f.total_kgs, 0).toFixed(2)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </Box>
        </Paper>
      )}

      <Typography sx={{ fontSize: 11.5, color: '#64748b', mt: 1 }}>
        💡 Click any FCL row to see the full manifest with all pallets, container info, and export options.
      </Typography>
    </Box>
  );
}

// ============================================================================
//  FCL Detail View
// ============================================================================
function FCLDetailView({ fcl, loading, onBack, showToast }) {
  const handleExport = () => {
    if (!fcl) return;
    const headers = ['Pallet ID', 'Source RPK', 'Product', 'Grade', 'Pack Size', 'Brand',
                     'Lot No', 'Day Code', 'Trace ID', 'NAV ID', 'Product ID', 'Pack Style',
                     'Cartons', 'Total KG', 'Total LB'];
    const rows = fcl.pallets.map(p => [
      p.pallet_id, p.prior_gateout_no || '',
      p.product, p.grade, p.pack_size, p.brand,
      p.lot_no, p.day_code, p.trace_id, p.nav_id, p.product_id, p.pack_style,
      p.cartons, p.total_kgs, p.total_pounds,
    ]);
    const metaRows = [
      ['FCL No',           fcl.fcl_no],
      ['Status',           fcl.status],
      ['Container No',     fcl.container_no],
      ['Seal No',          fcl.seal_no],
      ['Buyer',            fcl.buyer],
      ['Destination Port', fcl.destination_port],
      ['Stuffing Location',fcl.stuffing_location],
      ['Departure Date',   fcl.departure_date],
      ['Departure Time',   fcl.departure_time],
      ['Vehicle No',       fcl.vehicle_no],
      ['Driver Name',      fcl.driver_name],
      ['Driver Mobile',    fcl.driver_mobile],
      ['Submitted By',     fcl.submitted_by],
      ['Submitted At',     fcl.submitted_at],
      ['Approved By',      fcl.approved_by || '—'],
      ['Approved At',      fcl.approved_at || '—'],
      ['Total Pallets',    fcl.total_pallets],
      ['Total Cartons',    fcl.total_cartons],
      ['Total KG',         fcl.total_kgs],
      ['Total LB',         fcl.total_pounds],
      ['Source RPK Docs',  fcl.source_docs.join(', ')],
    ];
    exportExcel({
      filename: `manifest_${fcl.fcl_no}_${todayStr()}.xlsx`,
      sheets: [
        { name: 'Manifest Header', headers: ['Field', 'Value'], rows: metaRows },
        { name: 'Pallet List', headers, rows },
      ],
    });
    showToast?.({ message: '📥 Excel manifest exported', severity: 'success' });
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!fcl) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button startIcon={<BackIcon />} variant="outlined" size="small" onClick={onBack}
                sx={{ textTransform: 'none' }}>
          Back to FCL list
        </Button>
        <Box sx={{ ml: 1.5 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>FCL Manifest Detail</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
            Export-ready document for buyer customs paperwork
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button startIcon={<ExportIcon />} variant="contained" size="small" onClick={handleExport}
                  sx={{
                    background: 'linear-gradient(90deg, #16a34a, #15803d)',
                    color: 'white', fontWeight: 700, textTransform: 'none',
                  }}>
            Export Excel
          </Button>
          <Button startIcon={<PrintIcon />} variant="contained" size="small" onClick={handlePrint}
                  sx={{
                    background: 'linear-gradient(90deg, #1F618D, #3b82f6)',
                    color: 'white', fontWeight: 700, textTransform: 'none',
                  }}>
            Print PDF
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 2 }}>
        {/* Header */}
        <Box sx={{ p: 2.25, background: 'linear-gradient(135deg, #5b21b6, #7c3aed)', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: 12, opacity: 0.85, letterSpacing: 0.5 }}>
              SHIPMENT MANIFEST
            </Typography>
            <Chip label={fcl.status.replace('_', ' ')} size="small" sx={{
              bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, fontSize: 10.5,
            }} />
          </Box>
          <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 22 }}>
            {fcl.fcl_no}
          </Typography>
          <Typography sx={{ fontSize: 13, opacity: 0.9, mt: 0.5 }}>
            {fcl.buyer} · {fcl.destination_port}
          </Typography>
        </Box>

        {/* Container/buyer details */}
        <Box sx={{ p: 2, bgcolor: '#fafbfc', borderBottom: '1px solid #e2e8f0' }}>
          <Grid container spacing={2}>
            <MetaItem label="Container No"        value={fcl.container_no} mono />
            <MetaItem label="Seal No"             value={fcl.seal_no} mono />
            <MetaItem label="Destination Port"    value={fcl.destination_port} />
            <MetaItem label="Stuffing Location"   value={fcl.stuffing_location} />
            <MetaItem label="Departure Date / Time" value={`${fcl.departure_date} · ${fcl.departure_time}`} />
            <MetaItem label="Vehicle / Truck"     value={fcl.vehicle_no} mono />
            <MetaItem label="Driver"              value={fcl.driver_name}
                      sub={fcl.driver_mobile} />
            <MetaItem label="Source Documents"
                      value={fcl.source_docs.length > 0
                        ? <>{fcl.source_docs.map(d => <GoNoChip key={d} no={d} />)}</>
                        : '—'} />
          </Grid>
        </Box>

        {/* Audit info */}
        <Box sx={{ p: 2, bgcolor: 'white' }}>
          <Grid container spacing={2}>
            <MetaItem label="Submitted By" value={fcl.submitted_by}
                      sub={<Box sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{fcl.submitted_at}</Box>} />
            {fcl.approved_by && (
              <MetaItem label="Approved By"
                        value={<Box sx={{ color: '#15803d' }}>✓ {fcl.approved_by}</Box>}
                        sub={<Box sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{fcl.approved_at}</Box>} />
            )}
            {fcl.cancelled_at && (
              <MetaItem label="Cancelled"
                        value={<Box sx={{ color: '#991b1b' }}>✗ {fcl.cancelled_at}</Box>}
                        sub={fcl.cancellation_reason} />
            )}
            {!fcl.approved_by && !fcl.cancelled_at && (
              <MetaItem label="Approval Status"
                        value={<Box sx={{ color: '#92400e' }}>⏳ Awaiting approver email response</Box>} />
            )}
          </Grid>
        </Box>

        {/* Pallets */}
        <Box sx={{ p: 1.75, bgcolor: 'white' }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: '#475569',
            textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 1,
            '&::before': { content: '""', width: 3, height: 14, bgcolor: '#8E44AD', borderRadius: 0.25 },
          }}>
            📦 Pallet Manifest
            <Box component="span" sx={{ bgcolor: '#f5edf7', color: '#8E44AD', fontSize: 10.5, px: 1, borderRadius: 5, fontWeight: 700 }}>
              {fcl.total_pallets} pallets · {fcl.total_cartons.toLocaleString()} cartons
            </Box>
          </Typography>
        </Box>

        {fcl.pallets.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Pallet ID</TableCell>
                  <TableCell>Source RPK</TableCell>
                  <TableCell>Product · Grade · Pack</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Lot No</TableCell>
                  <TableCell>Day · Trace</TableCell>
                  <TableCell align="right">Cartons</TableCell>
                  <TableCell align="right">Total KG</TableCell>
                  <TableCell align="right">Total LB</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fcl.pallets.map((p, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <strong style={{ fontFamily: 'JetBrains Mono, monospace', color: '#1F618D' }}>
                        {p.pallet_id}
                      </strong>
                    </TableCell>
                    <TableCell><GoNoChip no={p.prior_gateout_no} prefix="RPK" /></TableCell>
                    <TableCell>
                      <strong>{p.product}</strong> · {p.grade} · {p.pack_size}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11.5 }}>{p.brand || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5 }}>
                      {p.lot_no || '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
                      {p.day_code || '—'} / {p.trace_id || '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{p.cartons}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{p.total_kgs.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{p.total_pounds.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ bgcolor: '#fafbfc' }}>
                  <TableCell colSpan={6} sx={{ fontWeight: 700, color: '#0f172a' }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#0f172a' }}>
                    {fcl.total_cartons.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#0f172a' }}>
                    {fcl.total_kgs.toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#0f172a' }}>
                    {fcl.total_pounds.toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </Box>
        ) : (
          <Box sx={{ p: 3, color: '#64748b', fontSize: 12.5, fontStyle: 'italic' }}>
            No pallets linked to this FCL.
          </Box>
        )}
      </Paper>
    </Box>
  );
}

function MetaItem({ label, value, sub, mono }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Typography sx={{
        fontSize: 10.5, color: '#64748b', fontWeight: 700,
        letterSpacing: 0.3, textTransform: 'uppercase',
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 13, fontWeight: 600, mt: 0.25,
        ...(mono && { fontFamily: 'JetBrains Mono, monospace' }),
      }} component="div">
        {value || '—'}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.25 }} component="div">
          {sub}
        </Typography>
      )}
    </Grid>
  );
}
